import type {
  StorageOccupancyStatus,
  StorageType,
  UtilizationBasis,
  ZoneOccupancyStatus,
} from './types';

/**
 * Storage occupancy status (server-computed from used m³ ÷ capacity):
 * labels, tones (pill/badge) and the fill colours the map paints bins with.
 * Shared by the Storage list and the Peta gudang canvas.
 */
export interface OccupancyStatusMeta {
  label: string;
  /** Badge tone, same vocabulary as `status.ts`. */
  tone: 'neutral' | 'info' | 'warning' | 'danger' | 'success';
  /** Canvas fill (hex) — hue by status, kept quiet per the GSM. */
  fill: string;
  /** Short hint for tooltips / legend. */
  hint: string;
}

export const OCCUPANCY_STATUS: Record<StorageOccupancyStatus, OccupancyStatusMeta> =
  {
    TERSEDIA: {
      label: 'Tersedia',
      tone: 'success',
      fill: '#F3F3F3',
      hint: 'Kosong atau belum terpakai',
    },
    TERISI: {
      label: 'Terisi',
      tone: 'info',
      fill: '#CDB9EE',
      hint: 'Terpakai ≤ 50 %',
    },
    HAMPIR_PENUH: {
      label: 'Hampir penuh',
      tone: 'warning',
      fill: '#9F7BD9',
      hint: 'Terpakai 51–80 %',
    },
    PENUH: {
      label: 'Penuh',
      tone: 'danger',
      fill: '#5E2AAC',
      hint: 'Terpakai 81–100 %',
    },
    OVER: {
      label: 'Over kapasitas',
      tone: 'danger',
      fill: '#D93025',
      hint: 'Terpakai > 100 %',
    },
    KAPASITAS_BELUM_DIATUR: {
      label: 'Kapasitas belum diatur',
      tone: 'neutral',
      fill: '#E6E6E6',
      hint: 'Ada stok tetapi kapasitas belum diset',
    },
  };

/**
 * Utilization status meta; `hint` is the rule behind it, built from the
 * payload's thresholds (and the basis, for "Kapasitas belum diatur").
 */
export interface ZoneOccupancyStatusMeta
  extends Omit<OccupancyStatusMeta, 'hint'> {
  hint: (t: { warn: number; full: number }, basis?: UtilizationBasis) => string;
}

/**
 * Status of a location / section / bin on the chosen basis — the "Utilisasi
 * lokasi" page's one definition. Thresholds come from the payload.
 */
export const ZONE_OCCUPANCY_STATUS: Record<
  ZoneOccupancyStatus,
  ZoneOccupancyStatusMeta
> = {
  KAPASITAS_BELUM_DIATUR: {
    label: 'Kapasitas belum diatur',
    tone: 'neutral',
    fill: '#E6E6E6',
    hint: (_t, b) =>
      b
        ? `Isi kapasitas ${UTILIZATION_BASIS[b].noun} bin di menu Storage`
        : 'Isi kapasitas bin di menu Storage',
  },
  KOSONG: {
    label: 'Kosong',
    tone: 'neutral',
    fill: '#F3F3F3',
    hint: () => 'Belum ada stok',
  },
  NORMAL: {
    label: 'Normal',
    tone: 'success',
    fill: '#CDB9EE',
    hint: (t) => `Di bawah ${t.warn} % terpakai`,
  },
  HAMPIR_PENUH: {
    label: 'Hampir penuh',
    tone: 'warning',
    fill: '#9F7BD9',
    hint: (t) => `${t.warn}–${t.full - 1} % terpakai: siapkan ruang`,
  },
  PENUH: {
    label: 'Penuh',
    tone: 'danger',
    fill: '#5E2AAC',
    hint: (t) => `${t.full} % ke atas: barang masuk tidak punya tempat`,
  },
};

/** "Normal < 85 % · Hampir penuh ≥ 85 % · Penuh ≥ 95 %" — the payload's thresholds. */
export const zoneThresholdsLegend = (t: { warn: number; full: number }): string =>
  `Normal < ${t.warn} % · Hampir penuh ≥ ${t.warn} % · Penuh ≥ ${t.full} %`;

/** Progress-bar colour per percent (eFishery board rule: 0 / ≤50 / ≤80 / >80). */
export const pctTone = (
  pct: number | null
): 'success' | 'warning' | 'danger' | 'neutral' => {
  if (pct === null) return 'neutral';
  if (pct <= 0) return 'success';
  if (pct <= 50) return 'warning';
  if (pct <= 80) return 'warning';
  return 'danger';
};

/** `1234.5678` → `"1.234,57"`; null → `"—"`. */
export const fmtCbm = (v: number | null | undefined): string =>
  v === null || v === undefined
    ? '—'
    : new Intl.NumberFormat('id-ID', { maximumFractionDigits: 3 }).format(v);

export const fmtPct = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : `${Math.round(v)}%`;

/** `20.25` → `"20,3"` (kg, 1 decimal at most); null → `"—"`. */
export const fmtKg = (v: number | null | undefined): string =>
  v === null || v === undefined
    ? '—'
    : new Intl.NumberFormat('id-ID', { maximumFractionDigits: 1 }).format(v);

/** Units as a whole number; null → `"—"`. */
export const fmtUnit = (v: number | null | undefined): string =>
  v === null || v === undefined
    ? '—'
    : new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(v);

/** Per basis: the switch label, the unit, the noun in copy, formatters. */
export interface UtilizationBasisMeta {
  /** Segmented-control label: "Volume (m³)". */
  label: string;
  unit: 'm³' | 'kg' | 'unit';
  /** In copy: "kapasitas volume", "kapasitas berat", "kapasitas unit". */
  noun: string;
  /** Indonesian locale, the basis' decimals. */
  fmt: (v: number | null | undefined) => string;
  /** Decimals kept in the CSV (dot decimal). */
  decimals: number;
}

export const UTILIZATION_BASIS: Record<UtilizationBasis, UtilizationBasisMeta> = {
  volume: { label: 'Volume (m³)', unit: 'm³', noun: 'volume', fmt: fmtCbm, decimals: 3 },
  weight: { label: 'Berat (kg)', unit: 'kg', noun: 'berat', fmt: fmtKg, decimals: 1 },
  unit: { label: 'Unit', unit: 'unit', noun: 'unit', fmt: fmtUnit, decimals: 0 },
};

export const UTILIZATION_BASES: UtilizationBasis[] = ['volume', 'weight', 'unit'];

/** `1.2` (m³) → `"1,2 m³"`. */
export const fmtAmount = (v: number, basis: UtilizationBasis): string =>
  `${UTILIZATION_BASIS[basis].fmt(v)} ${UTILIZATION_BASIS[basis].unit}`;

/** "{used} / {capacity} {unit}" — `"1,2 / 5 m³"`. */
export const usedOfCapacity = (
  f: { used: number; capacity: number },
  basis: UtilizationBasis
): string =>
  `${UTILIZATION_BASIS[basis].fmt(f.used)} / ${fmtAmount(f.capacity, basis)}`;

/** A CSV number on the basis' decimals with a dot decimal (`1.2`, not `1,2`). */
export const csvAmount = (v: number | null | undefined, basis: UtilizationBasis): string => {
  if (v === null || v === undefined || !Number.isFinite(v)) return '';
  return String(Number(v.toFixed(UTILIZATION_BASIS[basis].decimals)));
};

/** The location type tag ("Storage", "Karantina", "Staging"). */
export const LOCATION_TYPE_LABEL: Record<StorageType, string> = {
  STORAGE: 'Storage',
  QUARANTINE: 'Karantina',
  STAGING: 'Staging',
};

/** The utilization filter of the location list (URL `util`) → pct bounds. */
export type UtilizationFilter = 'all' | 'penuh' | 'hampir' | 'normal' | 'rendah';

export const UTILIZATION_FILTERS: {
  value: UtilizationFilter;
  label: string;
  minPct: number | null;
  maxPct: number | null;
}[] = [
  { value: 'all', label: 'Semua utilisasi', minPct: null, maxPct: null },
  { value: 'penuh', label: 'Penuh (≥ 95 %)', minPct: 95, maxPct: null },
  { value: 'hampir', label: 'Hampir penuh ke atas (≥ 85 %)', minPct: 85, maxPct: null },
  { value: 'normal', label: 'Normal (< 85 %)', minPct: null, maxPct: 84.9 },
  { value: 'rendah', label: 'Kosong / rendah (< 20 %)', minPct: null, maxPct: 19.9 },
];
